import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  shortcutPlatform,
  shortcutEventCode,
  isShortcutModifier,
  type ShortcutBinding,
} from "@/lib/shortcut-registry";
import { suspendShortcutDispatch } from "@/stores/shortcut-store";

// Windows masks the Start menu with an unmapped keystroke while a Win-key
// combination is held; it is not part of what the user pressed.
const UNMAPPED_KEY = "Unidentified";

function normalizeCode(event: KeyboardEvent): string {
  const code = shortcutEventCode(event);
  return shortcutPlatform() === "macos"
    ? code.replace(/^(Control|Shift|Alt|Meta)(Left|Right)$/, "$1")
    : code;
}

export function useShortcutRecorder(
  defaults: ShortcutBinding,
  allowGestures = true,
) {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [candidate, setCandidate] = useState<ShortcutBinding | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pressedLabel, setPressedLabel] = useState<string[]>([]);
  const pressed = useRef(new Set<string>());
  const lastTap = useRef<{ key: string; held: string[]; at: number } | null>(
    null,
  );
  const alive = useRef(true);
  const generation = useRef(0);
  const captureRef = useRef<HTMLButtonElement>(null);
  const { gesture, holdMs, doubleTapMs } = defaults;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current += 1;
      void suspendShortcutDispatch(false).catch(() => {});
    };
  }, []);

  const stop = useCallback(() => {
    generation.current += 1;
    setRecording(false);
    setStarting(false);
    pressed.current.clear();
    lastTap.current = null;
    setPressedLabel([]);
    void suspendShortcutDispatch(false).catch((reason: unknown) => {
      if (alive.current)
        setError(reason instanceof Error ? reason.message : String(reason));
    });
  }, []);

  useEffect(() => {
    if (!recording && !starting) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !captureRef.current?.contains(event.target)
      )
        stop();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("blur", stop);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("blur", stop);
    };
  }, [recording, starting, stop]);

  // Keys are captured on the window: WKWebView does not focus a button on
  // click, so the capture button itself may never receive a key event. A
  // layout effect, so no key can land between "recording" showing and listening.
  useLayoutEffect(() => {
    if (!recording) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        stop();
        return;
      }
      if (event.key === "Tab") {
        stop();
        return;
      }
      if (event.key === UNMAPPED_KEY) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      const key = normalizeCode(event);
      // A shortcut has one ordinary key; the latest one pressed is the one meant.
      if (!isShortcutModifier(key))
        for (const item of [...pressed.current])
          if (!isShortcutModifier(item)) pressed.current.delete(item);
      pressed.current.add(key);
      const keys = [...pressed.current];
      setPressedLabel(keys);
      if (gesture === "drag" || gesture === "click") {
        if (keys.every(isShortcutModifier)) setCandidate({ keys, gesture });
        return;
      }
      if (gesture === "hold" || keys.length > 1 || !isShortcutModifier(key)) {
        setCandidate({
          keys,
          gesture: gesture === "hold" ? "hold" : "chord",
          ...(gesture === "hold" ? { holdMs } : {}),
        });
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" ||
        event.key === "Tab" ||
        event.key === UNMAPPED_KEY
      )
        return;
      event.preventDefault();
      event.stopPropagation();
      const key = normalizeCode(event);
      // macOS delivers no keyup for other keys while Command is held.
      if (event.key === "Meta")
        for (const item of [...pressed.current])
          if (!isShortcutModifier(item)) pressed.current.delete(item);
      if (!pressed.current.has(key)) return;
      const held = [...pressed.current].filter((item) => item !== key);
      if (
        gesture !== "drag" &&
        gesture !== "click" &&
        gesture !== "hold" &&
        allowGestures &&
        isShortcutModifier(key) &&
        held.every(isShortcutModifier)
      ) {
        const now = performance.now();
        const previous = lastTap.current;
        if (
          previous?.key === key &&
          previous.held.join("|") === held.join("|") &&
          now - previous.at <= 500
        ) {
          setCandidate({
            keys: [key],
            gesture: "doubleTap",
            doubleTapMs: doubleTapMs ?? 450,
            ...(held.length ? { holdKeys: held } : {}),
          });
          lastTap.current = null;
        } else {
          lastTap.current = { key, held, at: now };
        }
      }
      pressed.current.delete(key);
    };
    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("keyup", handleKeyUp, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("keyup", handleKeyUp, true);
    };
  }, [recording, gesture, holdMs, doubleTapMs, allowGestures, stop]);

  async function start() {
    if (starting) return;
    const token = ++generation.current;
    setStarting(true);
    setError(null);
    try {
      await suspendShortcutDispatch(true);
      if (!alive.current || token !== generation.current) return;
      pressed.current.clear();
      lastTap.current = null;
      setCandidate(null);
      setPressedLabel([]);
      setRecording(true);
      captureRef.current?.focus({ preventScroll: true });
    } catch (reason) {
      if (alive.current)
        setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (alive.current && token === generation.current) setStarting(false);
    }
  }

  return {
    captureRef,
    recording,
    starting,
    candidate,
    error,
    pressedLabel,
    start,
    stop,
  };
}
